import React, { useRef, useEffect } from "react";
import {
  TABLE_FONT as REPORT_FONT,
  CELL_FG,
  CELL_FG_STRONG as TOTAL_FG,
  EVEN_BG,
  ODD_BG,
  TOTAL_BG as TOTAL_BG_SC,
  TOTAL_BG_ALT as TOTAL_BG2_SC,
  ROW_TOTAL_BG as ROW_BTOTAL_BG,
  NEG_COLOR as NEG_FG,
  ACCENT_COLOR as SC_ACCENT,
  TH_BG as TH_BG_SC,
  TH_TEXT as TH_TXT_SC,
  TH_BORDER as TH_BDR_SC,
  TH_SHADOW as TH_SH_SC,
  TH_SHADOW_ROW1 as TH_SH_ROW1_SC,
  Z_HEAD,
  Z_STICKY,
  TH_HEIGHT,
  ROW_HEIGHT,
  TH_BG_BAND,
} from "../../reportTableConfig";

/* ══════════════════════════════════════════════════════════════ */
/*  Shared styling + logic for Scenario YoY interactive tables     */
/*  Table tokens live in reportTableConfig.js                     */
/* ══════════════════════════════════════════════════════════════ */

export {
  CELL_FG,
  TOTAL_FG,
  EVEN_BG,
  ODD_BG,
  TOTAL_BG_SC,
  TOTAL_BG2_SC,
  ROW_BTOTAL_BG,
  NEG_FG,
  SC_ACCENT,
  TH_BG_SC,
  TH_TXT_SC,
  Z_HEAD,
  Z_STICKY,
};

export const TH_ROW2_TOP_BORDER = `1px solid ${TH_BDR_SC}`;

export const GH = TH_HEIGHT;
export const CH = TH_HEIGHT;
export const RH = ROW_HEIGHT;

/* ── header cell styles ── */
export const thCoreSC = (top, h, z, padding, row = 2) => ({
  position: "sticky",
  top,
  zIndex: z,
  height: h,
  lineHeight: 1.25,
  boxSizing: "border-box",
  background: TH_BG_SC,
  color: TH_TXT_SC,
  verticalAlign: "middle",
  padding,
  whiteSpace: "nowrap",
  overflow: "hidden",
  boxShadow: row === 1 ? TH_SH_ROW1_SC : TH_SH_SC,
  border: "none",
  ...(row === 2 ? { borderTop: TH_ROW2_TOP_BORDER } : {}),
});

/* ── data cell styles ── */
export const tdBaseSC = (bg, align = "right", fw = 400) => ({
  boxSizing: "border-box",
  background: bg,
  textAlign: align,
  fontWeight: fw,
  padding: "7px 8px",
  fontSize: 12,
  color: CELL_FG,
  whiteSpace: "nowrap",
  verticalAlign: "middle",
  fontVariantNumeric: "tabular-nums",
  borderBottom: "1px solid #E2E8F0",
});

/** Identity / text columns: wrap long names; no fixed width. */
export const tdTextSC = (bg, align = "left", fw = 400, isLast = false, maxW) => ({
  ...tdBaseSC(bg, align, fw),
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  fontVariantNumeric: "normal",
  ...(maxW != null ? { maxWidth: maxW } : {}),
  boxShadow: isLast ? "4px 0 8px -2px rgba(0,0,0,0.06)" : undefined,
});

export const tdFixedSC = (bg, col, fw = 400, isLast = false) =>
  tdTextSC(bg, col.align, fw, isLast, col.maxW);

export const tdTotalSC = (col, isLast = false) => ({
  ...tdFixedSC(TOTAL_BG_SC, col, 700, isLast),
  color: TOTAL_FG,
  borderBottom: "1px solid #CBD5E1",
});

export const tdTotalScrollSC = () => ({
  ...tdBaseSC(TOTAL_BG_SC, "right", 600),
  color: TOTAL_FG,
  borderBottom: "1px solid #CBD5E1",
});

/* ── card styles ── */
export const CARD_STYLE_SC = {
  background: "#FFFFFF",
  borderRadius: 12,
  border: "1px solid #EBEBEB",
  overflow: "visible",
  marginBottom: 20,
  marginTop: 16,
  boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
};
export const CARD_HEAD_STYLE_SC = {
  padding: "16px 20px 12px",
  borderBottom: "1px solid #E2E8F0",
  background: "#FFFFFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
};
export const CARD_TITLE_STYLE_SC = {
  fontSize: 14,
  fontWeight: 600,
  color: "#111827",
  letterSpacing: "-0.01em",
  fontFamily: REPORT_FONT,
  margin: 0,
};
export const CARD_META_STYLE_SC = {
  fontSize: 12,
  fontWeight: 400,
  color: "#6B7280",
  fontFamily: REPORT_FONT,
  lineHeight: 1.4,
  marginTop: 2,
};

/* ══════════════════════════════════════════════════════════════ */
/*  Column filter — operators                                     */
/* ══════════════════════════════════════════════════════════════ */
export const CT_OP_ENTRIES = [
  { op: "gt", sym: ">", label: "Greater than" },
  { op: "gte", sym: "≥", label: "At least" },
  { op: "lt", sym: "<", label: "Less than" },
  { op: "lte", sym: "≤", label: "At most" },
  { op: "eq", sym: "=", label: "Equal to" },
  { op: "neq", sym: "≠", label: "Not equal to" },
];

export function ctApplyNumFilter(v, f) {
  if (!f?.value) return true;
  const n = Number(v);
  const fv = Number(f.value);
  if (isNaN(n) || isNaN(fv)) return false;
  switch (f.op) {
    case "gt":
      return n > fv;
    case "gte":
      return n >= fv;
    case "lt":
      return n < fv;
    case "lte":
      return n <= fv;
    case "eq":
      return n === fv;
    case "neq":
      return n !== fv;
    default:
      return true;
  }
}

/* ── filter popover styles ── */
export const fpBase = {
  position: "fixed",
  zIndex: 9999,
  background: "#fff",
  border: "1px solid #E2E8F0",
  borderRadius: 8,
  boxShadow: "0 8px 24px rgba(0,0,0,0.13)",
  padding: "12px",
  minWidth: 220,
};
export const fpLbl = {
  fontSize: 10,
  fontWeight: 700,
  color: "#6B7280",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  fontFamily: REPORT_FONT,
  marginBottom: 8,
  display: "block",
};
export const fpTxtI = {
  width: "100%",
  boxSizing: "border-box",
  height: 28,
  border: "1.5px solid #CBD5E1",
  borderRadius: 5,
  padding: "0 8px",
  fontSize: 12,
  color: "#374151",
  fontFamily: REPORT_FONT,
  outline: "none",
};
export const fpNumI = {
  flex: 1,
  height: 28,
  border: "1.5px solid #CBD5E1",
  borderRadius: 5,
  padding: "0 8px",
  fontSize: 12,
  color: "#4F46E5",
  fontFamily: REPORT_FONT,
  outline: "none",
  minWidth: 0,
};
export const fpOpBt = {
  width: 28,
  height: 28,
  border: "1.5px solid #CBD5E1",
  borderRadius: 5,
  background: "#fff",
  color: "#4F46E5",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  fontFamily: REPORT_FONT,
};
export const fpClr = {
  marginTop: 8,
  fontSize: 11,
  color: "#94A3B8",
  cursor: "pointer",
  background: "none",
  border: "none",
  padding: 0,
  fontFamily: REPORT_FONT,
  textDecoration: "underline",
  display: "block",
};
export const opDropS = {
  position: "fixed",
  zIndex: 10000,
  background: "#fff",
  border: "1px solid #E5E7EB",
  borderRadius: 8,
  boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
  overflow: "hidden",
  minWidth: 190,
};
export const opItm = (a) => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "7px 12px",
  fontSize: 12,
  cursor: "pointer",
  fontFamily: REPORT_FONT,
  background: a ? "#F5F3FF" : "transparent",
  color: a ? "#4F46E5" : "#374151",
  fontWeight: a ? 600 : 400,
});
export const ctFBtnSt = (a) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 16,
  height: 16,
  padding: 0,
  borderRadius: 3,
  border: "none",
  cursor: "pointer",
  background: a ? "rgba(165,180,252,0.18)" : "transparent",
  color: a ? "#A5B4FC" : TH_TXT_SC,
  opacity: a ? 1 : 0.5,
  flexShrink: 0,
});

/* ── pagination styles ── */
export const ctPagBar = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 20px",
  borderTop: "1px solid #E2E8F0",
  background: "#FAFAFA",
  gap: 12,
  flexWrap: "wrap",
};
export const ctPagTxt = {
  fontSize: 12,
  color: "#6B7280",
  fontFamily: REPORT_FONT,
  whiteSpace: "nowrap",
};
export const ctPSzSel = {
  height: 28,
  border: "1px solid #E2E8F0",
  borderRadius: 6,
  fontSize: 12,
  color: "#374151",
  padding: "0 6px",
  background: "#fff",
  cursor: "pointer",
  fontFamily: REPORT_FONT,
};
export const ctPBtn = (d) => ({
  minWidth: 30,
  height: 28,
  padding: "0 6px",
  border: "1px solid #E2E8F0",
  borderRadius: 6,
  background: "#fff",
  color: d ? "#94A3B8" : "#374151",
  fontSize: 12,
  cursor: d ? "default" : "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: REPORT_FONT,
  opacity: d ? 0.35 : 1,
});
export const ctPBtnA = {
  minWidth: 30,
  height: 28,
  padding: "0 6px",
  border: `1px solid ${TH_BG_BAND}`,
  borderRadius: 6,
  background: TH_BG_BAND,
  color: "#fff",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: REPORT_FONT,
};
export const ctPDots = {
  fontSize: 12,
  color: "#6B7280",
  padding: "0 4px",
  userSelect: "none",
};

/* ══════════════════════════════════════════════════════════════ */
/*  Icons                                                         */
/* ══════════════════════════════════════════════════════════════ */
export function FilterIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Pagination bar                                                */
/* ══════════════════════════════════════════════════════════════ */
export function YoYPagination({
  total,
  rangeStart,
  rangeEnd,
  pageSize,
  setPageSize,
  page,
  setPage,
  totalPages,
  pageNums,
  unitLabel = "rows",
}) {
  if (total <= 0) return null;
  return (
    <div style={ctPagBar}>
      <span style={ctPagTxt}>{`${rangeStart}–${rangeEnd} of ${total} ${unitLabel}`}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={ctPagTxt}>Per page</span>
        <select
          style={ctPSzSel}
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(1);
          }}
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
          <button
            style={ctPBtn(page === 1)}
            disabled={page === 1}
            onClick={() => setPage(1)}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M9 10L6 7l3-3M5 10V4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            style={ctPBtn(page === 1)}
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M8.5 10.5L5 7l3.5-3.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {pageNums.map((p, i) => (
            <React.Fragment key={p}>
              {i > 0 && pageNums[i - 1] < p - 1 && <span style={ctPDots}>…</span>}
              <button
                style={p === page ? ctPBtnA : ctPBtn(false)}
                onClick={() => setPage(p)}
              >
                {p}
              </button>
            </React.Fragment>
          ))}
          <button
            style={ctPBtn(page === totalPages)}
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M5.5 3.5L9 7l-3.5 3.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            style={ctPBtn(page === totalPages)}
            disabled={page === totalPages}
            onClick={() => setPage(totalPages)}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M5 10l3-3-3-3M9 10V4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export function buildPageNums(totalPages, page) {
  if (totalPages <= 7)
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  const s = new Set([1, totalPages, page, page - 1, page + 1, page - 2, page + 2]);
  return [...s].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
}

/* ══════════════════════════════════════════════════════════════ */
/*  Generic column show/hide picker                               */
/* ══════════════════════════════════════════════════════════════ */
export function ColumnPicker({ title, items, visible, onToggle }) {
  const [open, setOpen] = React.useState(false);
  const wrapRef = useRef(null);
  const hiddenCount = items.length - visible.length;
  const isActive = open || hiddenCount > 0;

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div
      ref={wrapRef}
      style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}
    >
      <button
        type="button"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          height: 34,
          padding: "0 12px",
          borderRadius: 6,
          fontFamily: REPORT_FONT,
          border: `1px solid ${isActive ? "#C7D2FE" : "#E2E8F0"}`,
          background: isActive ? "#EEF2FF" : "#fff",
          color: isActive ? "#4F46E5" : "#374151",
          fontSize: 12,
          fontWeight: 500,
          cursor: "pointer",
        }}
        onClick={() => setOpen((p) => !p)}
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="3" y="3" width="7" height="18" rx="1" />
          <rect x="14" y="3" width="7" height="18" rx="1" />
        </svg>
        Columns
        {hiddenCount > 0 && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              height: 18,
              padding: "0 7px",
              borderRadius: 10,
              background: "#4F46E5",
              color: "#fff",
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            {hiddenCount} Hidden
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            zIndex: 1200,
            background: "#fff",
            borderRadius: 10,
            border: "1px solid #E5E7EB",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            minWidth: 240,
            maxHeight: 420,
            overflowY: "auto",
            fontFamily: REPORT_FONT,
          }}
        >
          <div
            style={{
              padding: "8px 14px",
              background: "#F8FAFC",
              borderBottom: "1px solid #E2E8F0",
              fontSize: 12,
              fontWeight: 700,
              color: "#111827",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>{title}</span>
            <span style={{ fontSize: 11, color: "#6B7280" }}>
              {visible.length} of {items.length}
            </span>
          </div>
          <div style={{ overflowY: "auto", padding: "4px 0 8px" }}>
            {items.map((it) => {
              const checked = visible.includes(it.key);
              return (
                <label
                  key={it.key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "7px 14px",
                    cursor: "pointer",
                    fontSize: 12,
                    color: "#374151",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(it.key)}
                    style={{
                      width: 14,
                      height: 14,
                      accentColor: "#4F46E5",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  />
                  <span>{it.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
